// auth/SessionModeBridge.tsx — Firebase auth state → sessionMode flag.
// ─────────────────────────────────────────────────────────────────────────
// Mounted once inside App (under the redux Provider). THREE responsibilities
// now, and they run on DIFFERENT conditions. Read that before editing any.
//
//   1. REFETCH, on the FLIP. When the mode actually flips mid-session
//      (login or logout without a full reload), refetch the financial
//      profile and scenarios so redux state swaps to the new world instead
//      of leaking the old one across the boundary. The initial resolve does
//      NOT refetch — pages fetch on mount themselves (gated on auth
//      loading), and double-fetching on every cold start would be wasted
//      work.
//
//   2. MIGRATE WORKING COPIES, on EVERY SIGNED-IN RESOLVE. Moving Out drop
//      2. This one is deliberately NOT gated on the flip. A redirect-based
//      sign-in and the /auth/action landing (see useEmailVerification's
//      VERIFY_CONTINUE_URL) both arrive as an initial resolve, not a flip,
//      and a flip-gated migration would silently never fire for them.
//      Running on every signed-in resolve is safe because
//      migrateAnonymousWorkingCopies is idempotent by construction.
//
//   3. ADOPT THE ANONYMOUS PLAN, on every signed-in resolve, for the same
//      reason as 2 and it is the same trap. Added 2026-08-01, closing
//      finding 18: `hasAnonData`, `adoptAnonData` and `discardAnonData` had
//      existed since before the arc with NO IMPORTER ANYWHERE, so a person
//      who built a plan anonymously and signed up because of it watched it
//      disappear. `scenariosSlice` reads `activeScenariosKey()`, which flips
//      the instant auth resolves, and nothing ever read the anonymous side
//      again.
//
//      WHY IT CANNOT HANG OFF THE FLIP EITHER. The single most likely path
//      to this state is: build a plan, sign up, verify by email, land on
//      /auth/action. That is an initial resolve. A flip-gated adoption would
//      miss precisely the journey it exists for.
//
// It lives here rather than in Simulate because people sign in from
// Dashboard, and a migration that only fires on a Simulate visit orphans
// the key past the routing decision.
//
// Renders nothing, EXCEPT the conflict prompt. That is a real change to this
// file's contract and it is deliberate: the alternative is a piece of global
// state read by some other component, and the decision belongs beside the
// effect that discovers it.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthUser } from '../useAuthUser';
import { useAppDispatch } from '../../store/hooks';
import { fetchFinancialProfile } from '../../store/financialSlice';
import { fetchScenarios } from '../../store/scenariosSlice';
import {
	SCENARIOS_KEY,
	adoptAnonData,
	anonPlanCount,
	conflictResolved,
	discardAnonData,
	hasAnonData,
	markConflictResolved,
} from '../../lib/sessionMode';
import { adoptionAction } from '../../lib/anonAdoption';
import AnonAdoptionPrompt from '../../components/AnonAdoptionPrompt/AnonAdoptionPrompt';
import { browserStore } from '../../pages/Simulate/hooks/useWorkingCopyRecovery';
import {
	migrateAnonymousWorkingCopies,
	type KeyValueStore,
} from '../../pages/Simulate/workingCopyStorage';

/**
 * Plan ids in the SIGNED-IN world, read from the literal key rather than
 * through `activeScenariosKey()`.
 *
 * Mode-awareness is the thing being worked around here: the active key
 * flips the moment auth.currentUser is non-null, and this effect needs the
 * signed-in side specifically, whichever side is currently "active".
 *
 * Redux is deliberately not the source. The slice hydrates from whichever
 * key was active at store-construction time, which is the anonymous one on
 * a cold public load, and the whole point of this read is to be certain
 * which world it came from.
 */
function signedInPlanIds(store: KeyValueStore | null): string[] {
	if (store === null) return [];
	try {
		const raw = store.getItem(SCENARIOS_KEY);
		if (raw === null) return [];
		const parsed: unknown = JSON.parse(raw);
		if (typeof parsed !== 'object' || parsed === null) return [];
		const list = (parsed as { scenarios?: unknown }).scenarios;
		if (!Array.isArray(list)) return [];
		return list
			.map((s) =>
				typeof s === 'object' && s !== null
					? (s as { id?: unknown }).id
					: undefined,
			)
			.filter((id): id is string => typeof id === 'string');
	} catch {
		// Unparseable storage is a reason to migrate nothing, not a reason
		// to take the shell down.
		return [];
	}
}

const SessionModeBridge = (): JSX.Element | null => {
	const { user, loading } = useAuthUser();
	const dispatch = useAppDispatch();
	// undefined = auth not resolved yet; thereafter tracks anonymity.
	const wasAnon = useRef<boolean | undefined>(undefined);

	// The conflict prompt. Null means nothing to ask.
	const [conflict, setConflict] = useState<{
		uid: string;
		planCount: number;
	} | null>(null);
	const [busy, setBusy] = useState(false);

	/** Run the sequence and report. Shared by the silent path and the
	 *  prompt, so the two cannot diverge on what "adopt" means. */
	const runAdoption = useCallback(
		(uid: string): void => {
			const outcome = adoptAnonData();
			if (outcome.kind === 'adopted') {
				// Redux is still holding whichever world it hydrated from.
				// Refetch so the plans that just arrived are on screen: the
				// person is looking at the list they expect them in.
				dispatch(fetchScenarios());
				dispatch(fetchFinancialProfile());
			}
			// A failure needs NO cleanup. The anonymous keys are untouched
			// and the next signed-in resolve tries again, which is why this
			// effect is not gated on the flip.
			markConflictResolved(uid);
		},
		[dispatch],
	);

	useEffect(() => {
		if (loading) return;
		const anon = user === null;
		const flipped =
			wasAnon.current !== undefined && wasAnon.current !== anon;
		wasAnon.current = anon;

		if (flipped) {
			// Mid-session login/logout: swap redux to the new world. The
			// thunks derive their keys from auth.currentUser, which has
			// already changed by the time this effect observes it.
			dispatch(fetchFinancialProfile());
			dispatch(fetchScenarios());
		}

		if (anon) return;

		// Synchronous on purpose, and NOT sequenced behind fetchScenarios.
		// An anonymous working copy drafts against an anonymous plan, which
		// is local-only and never on the server, so the server merge cannot
		// supply an id this needs. What it needs is whatever adopted the
		// anonymous plans locally, and that has already written by now.
		//
		// The outcome is not consumed yet. `contested` — both namespaces
		// holding a draft for one plan — is the case contract 4.4 says must
		// be ASKED, and there is no surface to ask on until drop 6 puts an
		// entrance on screen. Until then those keys are left untouched,
		// which is the non-destructive reading of "ask".
		const store = browserStore();
		migrateAnonymousWorkingCopies(
			store,
			signedInPlanIds(store),
			Date.now(),
		);

		// ── Adoption. Ruled 2026-08-01. ──
		// The ORDER of these checks is `adoptionAction`'s, not this file's,
		// so the policy is testable without a DOM and a second opinion about
		// it cannot appear here.
		const uid = user.uid;
		const action = adoptionAction({
			anonHasPlans: hasAnonData(),
			signedInHasPlans: signedInPlanIds(store).length > 0,
			conflictAlreadyResolved: conflictResolved(uid),
		});

		if (action.kind === 'adopt-silently') {
			// No conflict, so no question. The overwhelmingly common case is
			// one anonymous plan and a brand new account, where a dialog has
			// exactly one useful answer.
			runAdoption(uid);
			return;
		}

		if (action.kind === 'ask') {
			setConflict({ uid, planCount: anonPlanCount() });
			return;
		}

		// `nothing-to-do` and `leave-alone`. NEITHER CLEARS THE ANON KEYS.
		// An unanswered-again question is not permission to throw work away.
	}, [user, loading, dispatch, runAdoption]);

	if (conflict === null) return null;

	return (
		<AnonAdoptionPrompt
			anonPlanCount={conflict.planCount}
			busy={busy}
			onImport={() => {
				setBusy(true);
				runAdoption(conflict.uid);
				setBusy(false);
				setConflict(null);
			}}
			onDiscard={() => {
				discardAnonData();
				markConflictResolved(conflict.uid);
				setConflict(null);
			}}
		/>
	);
};

export default SessionModeBridge;
